import {z} from 'zod'
import {
    Category,
    CategoryFormat,
    StepKeyName,
    TransportingGuestsCheckAndMode,
    FormFieldKeyName,
    TransportingGuestsCheckAndModePartial,
    TransportingGuestsMode
} from './types'
import {ExperienceShellMerchOutputSchema, ExperienceMerchandisingInputSchema} from '../experience-vetting/formSchemas'
import i18n from '@/translations/i18n'
import {
    DISCOUNT_MAX_VALUE,
    DISCOUNT_MIN_VALUE,
    DEFAULT_PRICE_LIMIT,
    MAX_PUBLIC_GROUP_SIZE
} from './components/pricing-discount-step/PricingDiscountStep'

export const ExperienceFormSchema = z.object({
    [FormFieldKeyName.enum.categoryKeyName]: Category,
    [FormFieldKeyName.enum.subCategoryKeyName]: CategoryFormat
})

export const BackgroundFormSchema = z.object({
    [FormFieldKeyName.enum.expertise_years]: z.coerce.number().int(),
    [FormFieldKeyName.enum.online_profile_links]: z
        .array(z.object({link: z.string().url().or(z.literal(''))}))
        .min(1)
        .optional(),
    [FormFieldKeyName.enum.residential_address]: z.string(),
    [FormFieldKeyName.enum.residential_country_region]: z.string().min(1),
    [FormFieldKeyName.enum.residential_address_extras]: z.string().optional(),
    [FormFieldKeyName.enum.residential_city]: z.string().optional(),
    [FormFieldKeyName.enum.residential_state]: z.string().optional(),
    [FormFieldKeyName.enum.residential_zip]: z.string().optional(),
    [FormFieldKeyName.enum.hosting_as_business]: z.boolean().default(false)
})

export const LocationFormSchema = z.object({
    [FormFieldKeyName.enum.country_region]: z.string(),
    [FormFieldKeyName.enum.location_address]: z.string(),
    [FormFieldKeyName.enum.location_address_extras]: z.string().optional(),
    [FormFieldKeyName.enum.location_city]: z.string(),
    [FormFieldKeyName.enum.location_state]: z.string(),
    [FormFieldKeyName.enum.location_zip]: z.string(),
    [FormFieldKeyName.enum.meeting_point_name]: z.string()
})

export const PricingDiscountsBaseFormSchema = z.object({
    [FormFieldKeyName.enum.limitedTimeDiscount]: z.coerce
        .number()
        .positive()
        .min(DISCOUNT_MIN_VALUE)
        .max(DISCOUNT_MAX_VALUE)
        .optional(),
    [FormFieldKeyName.enum.maximum_public_group_size]: z.coerce.number().int().max(MAX_PUBLIC_GROUP_SIZE).positive(),
    [FormFieldKeyName.enum.guest_individual_price]: z.coerce.number().int().positive().max(DEFAULT_PRICE_LIMIT).min(1),
    [FormFieldKeyName.enum.early_bird_discount]: z.coerce.boolean()
})

export const PricingDiscountsLargeGroupDiscountFormSchema = z.object({
    [FormFieldKeyName.enum.offer_large_group_discount]: z.coerce.boolean().optional(),
    [FormFieldKeyName.enum.discounts]: z.array(
        z.object({
            groupSize: z.coerce.number().int().positive().optional(),
            discountInPercentage: z.coerce.number().int().positive().max(100).optional()
        })
    )
})

export const PricingDiscountsNoLargeGroupDiscountFormSchema = z.object({
    [FormFieldKeyName.enum.offer_large_group_discount]: z.literal(false).optional(),
    [FormFieldKeyName.enum.discounts]: z.undefined()
})

export const PricingDiscountsInstancePrivateGroupsFormSchema = z.object({
    [FormFieldKeyName.enum.instance_private_groups]: z.literal(true).optional(),
    [FormFieldKeyName.enum.group_minimum_price]: z.coerce.number().int().positive().max(DEFAULT_PRICE_LIMIT).min(1)
})

export const PricingDiscountsNoInstancePrivateGroupsFormSchema = z.object({
    [FormFieldKeyName.enum.instance_private_groups]: z.literal(false).optional(),
    [FormFieldKeyName.enum.group_minimum_price]: z.undefined()
})

export const LegalFoodDrinkEntertainmentFormSchema = z.object({
    [FormFieldKeyName.enum.categoryKeyName]: Category.exclude([Category.enum.food_drink, Category.enum.entertainment]),
    [FormFieldKeyName.enum.involves_national_park]: z.boolean(),
    [FormFieldKeyName.enum.host_serve_alcohol]: z.undefined(),
    [FormFieldKeyName.enum.is_licensed_establishment]: z.undefined(),
    [FormFieldKeyName.enum.host_serve_food]: z.undefined(),
    [FormFieldKeyName.enum.prepared_in_restaurant]: z.undefined(),
    [FormFieldKeyName.enum.involves_point_of_interest]: z.boolean(),
    [FormFieldKeyName.enum.host_discusses_poi_significance]: z.boolean().optional()
})

export const LegalFoodDrinkFormSchema = z.object({
    [FormFieldKeyName.enum.categoryKeyName]: z.literal(Category.enum.food_drink),
    [FormFieldKeyName.enum.host_serve_food]: z.boolean(),
    [FormFieldKeyName.enum.prepared_in_restaurant]: z.boolean(),
    [FormFieldKeyName.enum.host_serve_alcohol]: z.boolean(),
    [FormFieldKeyName.enum.is_licensed_establishment]: z.boolean(),
    [FormFieldKeyName.enum.involves_national_park]: z.undefined(),
    [FormFieldKeyName.enum.involves_point_of_interest]: z.undefined(),
    [FormFieldKeyName.enum.host_discusses_poi_significance]: z.undefined()
})

export const LegalEntertainmentFormSchema = z.object({
    [FormFieldKeyName.enum.categoryKeyName]: z.literal(Category.enum.entertainment),
    [FormFieldKeyName.enum.host_serve_alcohol]: z.boolean(),
    [FormFieldKeyName.enum.is_licensed_establishment]: z.boolean(),
    [FormFieldKeyName.enum.involves_national_park]: z.undefined(),
    [FormFieldKeyName.enum.involves_point_of_interest]: z.undefined(),
    [FormFieldKeyName.enum.host_discusses_poi_significance]: z.undefined(),
    [FormFieldKeyName.enum.host_serve_food]: z.undefined(),
    [FormFieldKeyName.enum.prepared_in_restaurant]: z.undefined()
})

export const MerchandisingFormSchema = z.object({
    ...ExperienceShellMerchOutputSchema.shape,
    ...ExperienceMerchandisingInputSchema.shape
})

export const ItineraryFormSchema = z.object({})

export const ShellPartialFormSchema = z.object({
    [StepKeyName.enum.experience]: ExperienceFormSchema.partial(),
    [StepKeyName.enum.background]: BackgroundFormSchema.partial(),
    [StepKeyName.enum.pricing_discounts]: PricingDiscountsBaseFormSchema.partial()
        .and(
            PricingDiscountsLargeGroupDiscountFormSchema.partial().or(
                PricingDiscountsNoLargeGroupDiscountFormSchema.partial()
            )
        )
        .and(
            PricingDiscountsInstancePrivateGroupsFormSchema.partial().or(
                PricingDiscountsNoInstancePrivateGroupsFormSchema.partial()
            )
        )
        .superRefine((data, ctx) => {
            if (data.offer_large_group_discount && data.maximum_public_group_size) {
                data?.discounts?.forEach((discount, index: number) => {
                    if (
                        discount?.groupSize &&
                        discount.groupSize > (data?.maximum_public_group_size || MAX_PUBLIC_GROUP_SIZE)
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['discounts', index, 'groupSize']
                        })
                    }
                })
            }
        }),
    [StepKeyName.enum.merchandising]: MerchandisingFormSchema.partial(),
    [StepKeyName.enum.location]: LocationFormSchema.partial(),
    [StepKeyName.enum.itinerary]: ItineraryFormSchema.partial(),
    [StepKeyName.enum.settings]: z.undefined(),
    [StepKeyName.enum.legal]: TransportingGuestsCheckAndModePartial.and(
        LegalFoodDrinkEntertainmentFormSchema.partial()
            .or(LegalFoodDrinkFormSchema.partial())
            .or(LegalEntertainmentFormSchema.partial())
    )
})
export type ShellPartialFormSchema = z.infer<typeof ShellPartialFormSchema>
export type ShellFormDefaultValues = {
    [ShellFormSchemaKey in keyof ShellPartialFormSchema]: {
        [Key in keyof ShellPartialFormSchema[ShellFormSchemaKey]]:
            | ShellPartialFormSchema[ShellFormSchemaKey][Key]
            | undefined
    }
}

export const ShellFormSchema = z.object({
    [StepKeyName.enum.experience]: ExperienceFormSchema,
    [StepKeyName.enum.background]: BackgroundFormSchema,
    [StepKeyName.enum.pricing_discounts]: PricingDiscountsBaseFormSchema.and(
        PricingDiscountsLargeGroupDiscountFormSchema.or(PricingDiscountsNoLargeGroupDiscountFormSchema)
    ).and(PricingDiscountsInstancePrivateGroupsFormSchema.or(PricingDiscountsNoInstancePrivateGroupsFormSchema)),
    [StepKeyName.enum.merchandising]: MerchandisingFormSchema,
    [StepKeyName.enum.location]: LocationFormSchema,
    [StepKeyName.enum.itinerary]: ItineraryFormSchema,
    [StepKeyName.enum.legal]: TransportingGuestsCheckAndMode.and(
        LegalFoodDrinkEntertainmentFormSchema.or(LegalFoodDrinkFormSchema).or(LegalEntertainmentFormSchema)
    )
})
export type ShellFormSchema = z.infer<typeof ShellFormSchema>

export const ShellUpgradeFormSchema = z.object({
    [StepKeyName.enum.experience]: z.object({
        [FormFieldKeyName.enum.categoryKeyName]: Category.exclude([Category.enum.generic]),
        [FormFieldKeyName.enum.subCategoryKeyName]: CategoryFormat
    }),
    [StepKeyName.enum.background]: z.object({
        [FormFieldKeyName.enum.expertise_years]: z.coerce.number().int().min(1),
        [FormFieldKeyName.enum.online_profile_links]: z.array(z.object({link: z.string().url()})).min(1),
        [FormFieldKeyName.enum.residential_address]: z.string().min(1),
        [FormFieldKeyName.enum.residential_country_region]: z.string().min(1),
        [FormFieldKeyName.enum.residential_address_extras]: z.string().optional(),
        [FormFieldKeyName.enum.residential_city]: z.string().min(1),
        [FormFieldKeyName.enum.residential_state]: z.string().min(1),
        [FormFieldKeyName.enum.residential_zip]: z.string().min(1),
        [FormFieldKeyName.enum.hosting_as_business]: z.boolean()
    }),
    [StepKeyName.enum.merchandising]: MerchandisingFormSchema,
    [StepKeyName.enum.location]: z.object({
        [FormFieldKeyName.enum.country_region]: z.string().min(1),
        [FormFieldKeyName.enum.location_address]: z.string().min(1),
        [FormFieldKeyName.enum.location_address_extras]: z.string().optional(),
        [FormFieldKeyName.enum.location_city]: z.string().min(1),
        [FormFieldKeyName.enum.location_state]: z.string().min(1),
        [FormFieldKeyName.enum.location_zip]: z.string().min(1),
        [FormFieldKeyName.enum.meeting_point_name]: z.string().min(1)
    }),
    [StepKeyName.enum.itinerary]: ItineraryFormSchema,
    [StepKeyName.enum.pricing_discounts]: z
        .object({
            [FormFieldKeyName.enum.limitedTimeDiscount]: z.coerce
                .number()
                .positive()
                .min(DISCOUNT_MIN_VALUE)
                .max(DISCOUNT_MAX_VALUE)
                .optional(),
            [FormFieldKeyName.enum.maximum_public_group_size]: z.coerce
                .number()
                .int()
                .max(MAX_PUBLIC_GROUP_SIZE)
                .positive(),
            [FormFieldKeyName.enum.guest_individual_price]: z.coerce
                .number()
                .int()
                .positive()
                .max(DEFAULT_PRICE_LIMIT)
                .min(1),
            [FormFieldKeyName.enum.early_bird_discount]: z.boolean(),
            [FormFieldKeyName.enum.instance_private_groups]: z.boolean(),
            [FormFieldKeyName.enum.group_minimum_price]: z.coerce.number().optional(),
            [FormFieldKeyName.enum.offer_large_group_discount]: z.boolean(),
            [FormFieldKeyName.enum.discounts]: z
                .array(
                    z.object({
                        groupSize: z.coerce.number().int().positive(),
                        discountInPercentage: z.coerce.number().int().positive().max(100)
                    })
                )
                .optional()
        })
        .partial()
        .superRefine((data, ctx) => {
            if (data.offer_large_group_discount && data.maximum_public_group_size) {
                data?.discounts?.forEach((discount, index: number) => {
                    if (
                        discount?.groupSize &&
                        discount.groupSize > (data?.maximum_public_group_size || MAX_PUBLIC_GROUP_SIZE)
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['discounts', index, 'groupSize']
                        })
                    }
                })
            }
        }),
    /* .superRefine((data, ctx) => {
            if (data.instance_private_groups && !data.group_minimum_price) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['group_minimum_price'],
                    message: i18n.t('errors:required')
                })
            }
            if (data.instance_private_groups && data.always_require_minimum_price == null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['always_require_minimum_price'],
                    message: i18n.t('errors:required')
                })
            }
            if (data.offer_large_group_discount && !data.discounts) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['discounts'],
                    message: i18n.t('errors:required')
                })
            }
        }) */
    /* .superRefine((data, ctx) => {
            if (data.guest_bring_something && (!data.guest_bringing_items || data.guest_bringing_items.length == 0)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['guest_bringing_items'],
                    message: i18n.t('errors:required')
                })
            }
            if (
                data.have_accessibility_features &&
                (!data.accessibility_features || data.accessibility_features.length == 0)
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['accessibility_features'],
                    message: i18n.t('errors:required')
                })
            }
        }) */ [StepKeyName.enum.legal]: z
        .object({
            [FormFieldKeyName.enum.categoryKeyName]: Category.exclude([Category.enum.generic]),
            [FormFieldKeyName.enum.involves_national_park]: z.boolean().optional(),
            [FormFieldKeyName.enum.involves_point_of_interest]: z.boolean().optional(),
            [FormFieldKeyName.enum.host_discusses_poi_significance]: z.boolean().optional(),
            [FormFieldKeyName.enum.host_transports_guests]: z.boolean(),
            [FormFieldKeyName.enum.host_transporting_guests_method]: z.array(TransportingGuestsMode).optional(),
            [FormFieldKeyName.enum.host_serve_food]: z.boolean().optional(),
            [FormFieldKeyName.enum.prepared_in_restaurant]: z.boolean().optional(),
            [FormFieldKeyName.enum.host_serve_alcohol]: z.boolean().optional(),
            [FormFieldKeyName.enum.is_licensed_establishment]: z.boolean().optional()
        })
        .superRefine((data, ctx) => {
            if (
                data.host_transports_guests &&
                (!data.host_transporting_guests_method || data.host_transporting_guests_method.length == 0)
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['host_transporting_guests_method'],
                    message: i18n.t('errors:required')
                })
            }
            if (
                data.categoryKeyName == Category.enum.nature_outdoors ||
                data.categoryKeyName == Category.enum.art_design ||
                data.categoryKeyName == Category.enum.fitness_wellness ||
                data.categoryKeyName == Category.enum.history_culture
            ) {
                if (data.involves_national_park == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['involves_national_park'],
                        message: i18n.t('errors:required')
                    })
                }
                if (data.involves_point_of_interest == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['involves_point_of_interest'],
                        message: i18n.t('errors:required')
                    })
                }
                if (data.involves_point_of_interest && data.host_discusses_poi_significance == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['host_discusses_poi_significance'],
                        message: i18n.t('errors:required')
                    })
                }
            }
            if (data.categoryKeyName == Category.enum.food_drink) {
                if (data.host_serve_food == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['host_serve_food'],
                        message: i18n.t('errors:required')
                    })
                }
                if (data.host_serve_food && data.prepared_in_restaurant == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['prepared_in_restaurant'],
                        message: i18n.t('errors:required')
                    })
                }
                if (data.host_serve_alcohol == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['host_serve_alcohol'],
                        message: i18n.t('errors:required')
                    })
                }
                if (data.host_serve_alcohol && data.is_licensed_establishment == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['is_licensed_establishment'],
                        message: i18n.t('errors:required')
                    })
                }
            }
            if (data.categoryKeyName == Category.enum.entertainment) {
                if (data.host_serve_alcohol == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['host_serve_alcohol'],
                        message: i18n.t('errors:required')
                    })
                }
                if (data.host_serve_alcohol && data.is_licensed_establishment == null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['is_licensed_establishment'],
                        message: i18n.t('errors:required')
                    })
                }
            }
        })
})
export type ShellUpgradeFormSchema = z.infer<typeof ShellUpgradeFormSchema>

export type EditExperiencePayload = Partial<ShellPartialFormSchema[keyof ShellPartialFormSchema]>
